/**
 * Pure helpers: wholesale trade signals on Contact Manager rows (orders, last online, account age).
 * Overlay is keyed by email. Do not write live signals onto Firestore recipients except membershipMeta
 * snapshots taken at Transfer / New list.
 */

import {
  countOrdersInWindow,
  distinctIsoWeeksInLookback,
  emptyOrderUserStats,
} from '../services/segmentOrderIndex';
import { daysSinceMs, toMsFromFirestoreValue } from '../services/segmentEvalTimestamps';

const MS_DAY = 24 * 60 * 60 * 1000;

export const TRADE_FILTER_ALL = 'all';

export const ORDER_FILTER = Object.freeze({
  ALL: 'all',
  ZERO: 'zero',
  ONE: 'one',
  TWO_TO_FIVE: 'two_to_five',
  SIX_PLUS: 'six_plus',
  ONE_PLUS_90D: 'one_plus_90d',
});

export const LAST_ORDER_FILTER = Object.freeze({
  ALL: 'all',
  D7: '7d',
  D30: '30d',
  D90: '90d',
  QUIET_90: 'quiet_90',
});

export const LAST_ONLINE_FILTER = Object.freeze({
  ALL: 'all',
  D7: '7d',
  D30: '30d',
  NEVER: 'never',
});

export const ACCOUNT_AGE_FILTER = Object.freeze({
  ALL: 'all',
  UNDER_30: 'under_30',
  D30_180: '30_180',
  Y1_PLUS: '1y_plus',
  NO_ACCOUNT: 'no_account',
});

export const TRADE_BAND_FILTER = Object.freeze({
  ALL: 'all',
  WEEKLY: 'weekly_trader',
  REGULAR: 'regular',
  ONE_OFF_QUIET: 'one_off_quiet',
  NEVER_ORDERED: 'never_ordered',
  ONLINE_NO_ORDER: 'online_no_order',
});

export const TRADE_SORT = Object.freeze({
  COMPANY: 'company',
  NAME: 'name',
  STAGE: 'stage',
  ORDERS: 'orders',
  LAST_ORDER: 'last_order',
  LAST_ONLINE: 'last_online',
  ACCOUNT_AGE: 'account_age',
});

export const TRADE_BAND_LABELS = Object.freeze({
  weekly_trader: 'Weekly trader',
  regular: 'Regular',
  one_off_quiet: 'One-off quiet',
  never_ordered: 'Never ordered',
  online_no_order: 'Online, no order',
});

const cleanEmail = (v) => (v == null ? '' : String(v).trim().toLowerCase());

/**
 * @returns {object}
 */
export function emptyAccountSignal() {
  return {
    orderCount: 0,
    lastOrderMs: null,
    lastOnlineMs: null,
    accountCreatedMs: null,
    hasAccount: false,
    userId: null,
    weeksWithOrder8: 0,
    ordersIn90d: 0,
    tradeBand: null,
  };
}

/**
 * @param {object} signal
 * @param {number} nowMs
 */
export function resolveTradeBand(signal, nowMs) {
  if (!signal) return null;
  const orderCount = Number(signal.orderCount) || 0;
  const lastOrderDays = daysSinceMs(signal.lastOrderMs, nowMs);
  const lastOnlineDays = daysSinceMs(signal.lastOnlineMs, nowMs);
  const weeks = Number(signal.weeksWithOrder8) || 0;

  if (signal.hasAccount && orderCount === 0) return 'never_ordered';
  if (
    lastOnlineDays != null &&
    lastOnlineDays <= 14 &&
    (signal.lastOrderMs == null || (lastOrderDays != null && lastOrderDays > 30))
  ) {
    return 'online_no_order';
  }
  if (orderCount === 1 && lastOrderDays != null && lastOrderDays >= 90) return 'one_off_quiet';
  if (weeks >= 6 && lastOrderDays != null && lastOrderDays <= 21) return 'weekly_trader';
  if (orderCount >= 2) return 'regular';
  return null;
}

/**
 * @param {number|null} ms
 * @param {number} nowMs
 * @returns {string|null}
 */
export function formatRelativeDays(ms, nowMs) {
  const d = daysSinceMs(ms, nowMs);
  if (d == null) return null;
  if (d <= 0) return 'today';
  if (d === 1) return '1d';
  if (d < 14) return `${d}d`;
  if (d < 60) return `${Math.round(d / 7)}w`;
  if (d < 730) return `${Math.round(d / 30)}mo`;
  const y = Math.round(d / 365);
  return y === 1 ? '1y' : `${y}y`;
}

/**
 * @param {object|null} signal
 * @param {number} nowMs
 * @returns {string}
 */
export function formatTradeSignalLine(signal, nowMs) {
  if (!signal) return '';
  const parts = [];
  const n = Number(signal.orderCount) || 0;
  if (!signal.hasAccount && n === 0) {
    parts.push('no wholesale account');
  } else if (n === 0) {
    parts.push('never ordered');
  } else {
    parts.push(n === 1 ? '1 order' : `${n} orders`);
  }
  if (signal.lastOrderMs) {
    parts.push(`last order ${formatRelativeDays(signal.lastOrderMs, nowMs)}`);
  }
  if (signal.lastOnlineMs) {
    parts.push(`last online ${formatRelativeDays(signal.lastOnlineMs, nowMs)}`);
  } else if (signal.hasAccount) {
    parts.push('never online');
  }
  if (signal.accountCreatedMs) {
    parts.push(`account ${formatRelativeDays(signal.accountCreatedMs, nowMs)}`);
  }
  return parts.join(' · ');
}

function cartActivityMs(cart) {
  if (!cart || typeof cart !== 'object') return null;
  return (
    toMsFromFirestoreValue(cart.lastActivityAt) ||
    toMsFromFirestoreValue(cart.lastActiveAt) ||
    toMsFromFirestoreValue(cart.updatedAt) ||
    toMsFromFirestoreValue(cart.createdAt)
  );
}

/**
 * Build email -> signal map from already-fetched indexes (no Firestore).
 * @param {object} args
 * @param {object[]} args.users
 * @param {{ byUid: Map, byEmail: Map }|null} args.orderIndex
 * @param {Map<string, number>} args.lastActiveByUid
 * @param {Map<string, object>} args.latestCartByUid
 * @param {number} args.nowMs
 * @returns {Map<string, object>}
 */
export function buildSignalsByEmail({
  users = [],
  orderIndex = null,
  lastActiveByUid = new Map(),
  latestCartByUid = new Map(),
  nowMs = Date.now(),
} = {}) {
  /** @type {Map<string, object>} */
  const byEmail = new Map();
  const index = orderIndex || { byUid: new Map(), byEmail: new Map() };

  const upsert = (email, patch) => {
    const key = cleanEmail(email);
    if (!key) return;
    const prev = byEmail.get(key) || emptyAccountSignal();
    const next = { ...prev, ...patch };
    if (next.lastOrderMs == null && prev.lastOrderMs != null) next.lastOrderMs = prev.lastOrderMs;
    if (next.lastOnlineMs == null && prev.lastOnlineMs != null) next.lastOnlineMs = prev.lastOnlineMs;
    if ((next.orderCount || 0) < (prev.orderCount || 0)) next.orderCount = prev.orderCount;
    if ((next.ordersIn90d || 0) < (prev.ordersIn90d || 0)) next.ordersIn90d = prev.ordersIn90d;
    next.tradeBand = resolveTradeBand(next, nowMs);
    byEmail.set(key, next);
  };

  for (const u of users || []) {
    const email = u?.email || u?.Email;
    const uid = u?.id ? String(u.id) : '';
    const stats =
      (uid && index.byUid.get(uid)) ||
      (cleanEmail(email) && index.byEmail.get(cleanEmail(email))) ||
      emptyOrderUserStats();
    const activeMs = uid ? lastActiveByUid.get(uid) || null : null;
    const cartMs = uid ? cartActivityMs(latestCartByUid.get(uid)) : null;
    const lastOnlineMs = Math.max(activeMs || 0, cartMs || 0) || null;
    const weeks = distinctIsoWeeksInLookback(stats, 8, nowMs).size;
    upsert(email, {
      orderCount: stats.lifetimeCount || 0,
      lastOrderMs: stats.lastOrderMs,
      lastOnlineMs,
      accountCreatedMs: toMsFromFirestoreValue(u?.createdAt),
      hasAccount: true,
      userId: uid || null,
      weeksWithOrder8: weeks,
      ordersIn90d: countOrdersInWindow(stats, 90, nowMs),
    });
  }

  for (const [email, stats] of index.byEmail || []) {
    const key = cleanEmail(email);
    if (!key || byEmail.has(key)) continue;
    const weeks = distinctIsoWeeksInLookback(stats, 8, nowMs).size;
    upsert(key, {
      orderCount: stats.lifetimeCount || 0,
      lastOrderMs: stats.lastOrderMs,
      lastOnlineMs: null,
      accountCreatedMs: null,
      hasAccount: false,
      userId: null,
      weeksWithOrder8: weeks,
      ordersIn90d: countOrdersInWindow(stats, 90, nowMs),
    });
  }

  return byEmail;
}

/**
 * @param {object} rec
 * @param {Map<string, object>} byEmail
 */
export function signalForRecipient(rec, byEmail) {
  const em = cleanEmail(rec?.email || rec?.Email);
  if (!em || !byEmail) return emptyAccountSignal();
  return byEmail.get(em) || emptyAccountSignal();
}

function inLastDays(ms, days, nowMs) {
  if (ms == null) return false;
  return nowMs - ms <= days * MS_DAY;
}

/**
 * @param {object} signal
 * @param {object} filters
 * @param {number} nowMs
 */
export function signalMatchesTradeFilters(signal, filters, nowMs) {
  const s = signal || emptyAccountSignal();
  const orderFilter = filters?.orderFilter || ORDER_FILTER.ALL;
  const lastOrderFilter = filters?.lastOrderFilter || LAST_ORDER_FILTER.ALL;
  const lastOnlineFilter = filters?.lastOnlineFilter || LAST_ONLINE_FILTER.ALL;
  const accountAgeFilter = filters?.accountAgeFilter || ACCOUNT_AGE_FILTER.ALL;
  const tradeBandFilter = filters?.tradeBandFilter || TRADE_BAND_FILTER.ALL;
  const n = Number(s.orderCount) || 0;

  if (orderFilter === ORDER_FILTER.ZERO && n !== 0) return false;
  if (orderFilter === ORDER_FILTER.ONE && n !== 1) return false;
  if (orderFilter === ORDER_FILTER.TWO_TO_FIVE && (n < 2 || n > 5)) return false;
  if (orderFilter === ORDER_FILTER.SIX_PLUS && n < 6) return false;
  if (orderFilter === ORDER_FILTER.ONE_PLUS_90D && (Number(s.ordersIn90d) || 0) < 1) return false;

  if (lastOrderFilter === LAST_ORDER_FILTER.D7 && !inLastDays(s.lastOrderMs, 7, nowMs)) return false;
  if (lastOrderFilter === LAST_ORDER_FILTER.D30 && !inLastDays(s.lastOrderMs, 30, nowMs)) return false;
  if (lastOrderFilter === LAST_ORDER_FILTER.D90 && !inLastDays(s.lastOrderMs, 90, nowMs)) return false;
  if (lastOrderFilter === LAST_ORDER_FILTER.QUIET_90) {
    if (n < 1) return false;
    const d = daysSinceMs(s.lastOrderMs, nowMs);
    if (d == null || d < 90) return false;
  }

  if (lastOnlineFilter === LAST_ONLINE_FILTER.D7 && !inLastDays(s.lastOnlineMs, 7, nowMs)) return false;
  if (lastOnlineFilter === LAST_ONLINE_FILTER.D30 && !inLastDays(s.lastOnlineMs, 30, nowMs)) return false;
  if (lastOnlineFilter === LAST_ONLINE_FILTER.NEVER && s.lastOnlineMs != null) return false;

  if (accountAgeFilter === ACCOUNT_AGE_FILTER.NO_ACCOUNT && s.hasAccount) return false;
  if (accountAgeFilter === ACCOUNT_AGE_FILTER.UNDER_30) {
    const d = daysSinceMs(s.accountCreatedMs, nowMs);
    if (d == null || d >= 30) return false;
  }
  if (accountAgeFilter === ACCOUNT_AGE_FILTER.D30_180) {
    const d = daysSinceMs(s.accountCreatedMs, nowMs);
    if (d == null || d < 30 || d > 180) return false;
  }
  if (accountAgeFilter === ACCOUNT_AGE_FILTER.Y1_PLUS) {
    const d = daysSinceMs(s.accountCreatedMs, nowMs);
    if (d == null || d < 365) return false;
  }

  if (tradeBandFilter !== TRADE_BAND_FILTER.ALL) {
    if ((s.tradeBand || resolveTradeBand(s, nowMs)) !== tradeBandFilter) return false;
  }

  return true;
}

/**
 * @param {object} filters
 */
export function tradeFiltersAreActive(filters) {
  if (!filters) return false;
  return Boolean(
    (filters.orderFilter && filters.orderFilter !== ORDER_FILTER.ALL) ||
      (filters.lastOrderFilter && filters.lastOrderFilter !== LAST_ORDER_FILTER.ALL) ||
      (filters.lastOnlineFilter && filters.lastOnlineFilter !== LAST_ONLINE_FILTER.ALL) ||
      (filters.accountAgeFilter && filters.accountAgeFilter !== ACCOUNT_AGE_FILTER.ALL) ||
      (filters.tradeBandFilter && filters.tradeBandFilter !== TRADE_BAND_FILTER.ALL),
  );
}

/**
 * Compact “Matches:” reasons for the row.
 * @param {object} args
 */
export function buildContactMatchReasons({
  signal,
  tradeFilters,
  includeTagSteps = [],
  includeListsOn = false,
  excludeListsOn = false,
  nowMs = Date.now(),
} = {}) {
  const reasons = [];
  const s = signal || emptyAccountSignal();
  const tf = tradeFilters || {};

  if (tf.orderFilter === ORDER_FILTER.ZERO) reasons.push('0 orders');
  if (tf.orderFilter === ORDER_FILTER.ONE) reasons.push('1 order');
  if (tf.orderFilter === ORDER_FILTER.TWO_TO_FIVE) reasons.push('2 to 5 orders');
  if (tf.orderFilter === ORDER_FILTER.SIX_PLUS) reasons.push('6+ orders');
  if (tf.orderFilter === ORDER_FILTER.ONE_PLUS_90D) reasons.push('ordered in last 90d');

  if (tf.lastOrderFilter === LAST_ORDER_FILTER.D7) reasons.push('ordered in 7d');
  if (tf.lastOrderFilter === LAST_ORDER_FILTER.D30) reasons.push('ordered in 30d');
  if (tf.lastOrderFilter === LAST_ORDER_FILTER.D90) reasons.push('ordered in 90d');
  if (tf.lastOrderFilter === LAST_ORDER_FILTER.QUIET_90) reasons.push('quiet 90d+');

  if (tf.lastOnlineFilter === LAST_ONLINE_FILTER.D7) reasons.push('online in 7d');
  if (tf.lastOnlineFilter === LAST_ONLINE_FILTER.D30) reasons.push('online in 30d');
  if (tf.lastOnlineFilter === LAST_ONLINE_FILTER.NEVER) reasons.push('never online');

  if (tf.accountAgeFilter === ACCOUNT_AGE_FILTER.UNDER_30) reasons.push('account under 30d');
  if (tf.accountAgeFilter === ACCOUNT_AGE_FILTER.D30_180) reasons.push('account 30 to 180d');
  if (tf.accountAgeFilter === ACCOUNT_AGE_FILTER.Y1_PLUS) reasons.push('account 1y+');
  if (tf.accountAgeFilter === ACCOUNT_AGE_FILTER.NO_ACCOUNT) reasons.push('no wholesale account');

  const band = tf.tradeBandFilter;
  if (band && band !== TRADE_BAND_FILTER.ALL) {
    reasons.push(TRADE_BAND_LABELS[band] || band);
  }

  const tagSteps = (includeTagSteps || []).filter((g) => Array.isArray(g) && g.length);
  if (tagSteps.length) {
    reasons.push(`tags: ${tagSteps.map((g) => g.join(' or ')).join(' and ')}`);
  }
  if (includeListsOn) reasons.push('on required list(s)');
  if (excludeListsOn) reasons.push('not on excluded list(s)');

  if (!reasons.length && s.tradeBand) {
    return [];
  }
  return reasons;
}

function recName(r) {
  return `${r?.firstName || r?.['First Name'] || ''} ${r?.lastName || r?.['Last Name'] || ''}`.trim();
}

/**
 * @param {object} a
 * @param {object} b
 * @param {string} sortKey
 * @param {(rec: object) => object} getSignal
 */
export function compareRecipientsByTradeSort(a, b, sortKey, getSignal) {
  const key = sortKey || TRADE_SORT.COMPANY;
  const sa = getSignal ? getSignal(a) : emptyAccountSignal();
  const sb = getSignal ? getSignal(b) : emptyAccountSignal();
  const company = (x) => String(x?.company || x?.['Company Name'] || '');
  const tie = () => company(a).localeCompare(company(b));

  if (key === TRADE_SORT.NAME) {
    const c = recName(a).localeCompare(recName(b));
    return c || tie();
  }
  if (key === TRADE_SORT.STAGE) {
    const c = String(a?.leadStatus || '').localeCompare(String(b?.leadStatus || ''));
    return c || tie();
  }
  if (key === TRADE_SORT.ORDERS) {
    const c = (sb.orderCount || 0) - (sa.orderCount || 0);
    return c || tie();
  }
  if (key === TRADE_SORT.LAST_ORDER) {
    const c = (sb.lastOrderMs || 0) - (sa.lastOrderMs || 0);
    return c || tie();
  }
  if (key === TRADE_SORT.LAST_ONLINE) {
    const c = (sb.lastOnlineMs || 0) - (sa.lastOnlineMs || 0);
    return c || tie();
  }
  if (key === TRADE_SORT.ACCOUNT_AGE) {
    const c = (sa.accountCreatedMs || Number.MAX_SAFE_INTEGER) - (sb.accountCreatedMs || Number.MAX_SAFE_INTEGER);
    return c || tie();
  }
  return company(a).localeCompare(company(b));
}

/**
 * Snapshot stamped onto a recipient at Transfer / New list (Firestore-safe).
 * @param {object} args
 */
export function buildMembershipMeta({
  addedBy = '',
  sourceListIds = [],
  filterSummary = '',
  matchReasons = [],
  signal = null,
  nowMs = Date.now(),
} = {}) {
  const s = signal || emptyAccountSignal();
  return {
    addedAt: new Date(nowMs).toISOString(),
    addedBy: addedBy || '',
    sourceListIds: Array.isArray(sourceListIds) ? sourceListIds.filter(Boolean) : [],
    filterSummary: filterSummary || '',
    matchReasons: Array.isArray(matchReasons) ? matchReasons.filter(Boolean).slice(0, 12) : [],
    signalSnapshot: {
      orderCount: Number(s.orderCount) || 0,
      lastOrderMs: s.lastOrderMs || null,
      lastOnlineMs: s.lastOnlineMs || null,
      accountCreatedMs: s.accountCreatedMs || null,
      tradeBand: s.tradeBand || null,
    },
  };
}

/**
 * One-line why for quick view / row when membershipMeta exists.
 * @param {object|null} meta
 */
export function formatMembershipMetaBrief(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const parts = [];
  if (Array.isArray(meta.matchReasons) && meta.matchReasons.length) {
    parts.push(meta.matchReasons.join(', '));
  } else if (meta.filterSummary) {
    parts.push(meta.filterSummary);
  }
  if (meta.addedAt) {
    const day = String(meta.addedAt).slice(0, 10);
    parts.push(day);
  }
  if (meta.addedBy) parts.push(meta.addedBy);
  return parts.join(' · ');
}

/**
 * Extra phrases for the governance filter summary line.
 * @param {object} filters
 */
export function formatTradeFilterSummary(filters) {
  if (!tradeFiltersAreActive(filters)) return '';
  const parts = [];
  const o = filters.orderFilter;
  if (o === ORDER_FILTER.ZERO) parts.push('Orders: 0');
  if (o === ORDER_FILTER.ONE) parts.push('Orders: 1');
  if (o === ORDER_FILTER.TWO_TO_FIVE) parts.push('Orders: 2 to 5');
  if (o === ORDER_FILTER.SIX_PLUS) parts.push('Orders: 6+');
  if (o === ORDER_FILTER.ONE_PLUS_90D) parts.push('Orders: 1+ in 90d');
  const lo = filters.lastOrderFilter;
  if (lo === LAST_ORDER_FILTER.D7) parts.push('Last order: 7d');
  if (lo === LAST_ORDER_FILTER.D30) parts.push('Last order: 30d');
  if (lo === LAST_ORDER_FILTER.D90) parts.push('Last order: 90d');
  if (lo === LAST_ORDER_FILTER.QUIET_90) parts.push('Last order: quiet 90d+');
  const on = filters.lastOnlineFilter;
  if (on === LAST_ONLINE_FILTER.D7) parts.push('Last online: 7d');
  if (on === LAST_ONLINE_FILTER.D30) parts.push('Last online: 30d');
  if (on === LAST_ONLINE_FILTER.NEVER) parts.push('Last online: never');
  const age = filters.accountAgeFilter;
  if (age === ACCOUNT_AGE_FILTER.UNDER_30) parts.push('Account age: under 30d');
  if (age === ACCOUNT_AGE_FILTER.D30_180) parts.push('Account age: 30 to 180d');
  if (age === ACCOUNT_AGE_FILTER.Y1_PLUS) parts.push('Account age: 1y+');
  if (age === ACCOUNT_AGE_FILTER.NO_ACCOUNT) parts.push('Account age: no account');
  const band = filters.tradeBandFilter;
  if (band && band !== TRADE_BAND_FILTER.ALL) parts.push(`Band: ${TRADE_BAND_LABELS[band] || band}`);
  return parts.join(' · ');
}
